/**
 * Rank incidents by how many over-NIOSH-REL exposure readings each accumulated,
 * so the "Peak gas exposures" KPI can drill down to the exact incidents that add
 * up to its number — most contributors first. Pure + deterministic: the count is
 * the same over-REL filter the KPI itself uses (one record = one firefighter on
 * one incident), and ties are broken by most-recent dispatch so the heaviest,
 * freshest call sits on top. See src/lib/carcinogen.ts for the REL definitions.
 */
export type OverRelExposure = { incident_id: string };

export type RankedIncident = { incident_id: string; count: number };

/**
 * Group already-over-REL exposure records by incident and order them by count
 * (descending), breaking ties with the supplied dispatch timestamp so the newest
 * call ranks first. `dispatchedAt` returns an ISO string (or "" when unknown).
 */
export function rankIncidentsByOverRel(
  records: OverRelExposure[],
  dispatchedAt: (incidentId: string) => string,
): RankedIncident[] {
  const counts = new Map<string, number>();
  for (const r of records) {
    counts.set(r.incident_id, (counts.get(r.incident_id) ?? 0) + 1);
  }
  return Array.from(counts, ([incident_id, count]) => ({
    incident_id,
    count,
  })).sort(
    (a, b) =>
      b.count - a.count ||
      dispatchedAt(b.incident_id).localeCompare(dispatchedAt(a.incident_id)),
  );
}
